import React, { useState, useEffect, useContext } from 'react'
import { useStyles } from './Styles'
import {
  Box,
  Grid,
  AppBar,
  IconButton,
  Toolbar as MdToolbar,
  List,
  ListItem,
  ListItemText,
  SwipeableDrawer,
  useScrollTrigger,
  Slide,
  Menu,
  MenuItem
} from '@material-ui/core'
import MenuIcon from '@material-ui/icons/Menu'
import { Settings, ArrowBack } from '@material-ui/icons'
import { Background, PointrLogo } from 'components'
import { MENU_LIST, MENU_IDS } from 'config/constants/navigation-menu'
import { InitiativesSelectorDialog } from 'smart-components/initiatives-selector-dialog'
import { pointrMinimalLogo } from 'assets/images'
import { IProps } from './Interface'
import Provider, { ToolbarContextProvider, ToolbarContext } from 'smart-components/toolbar/Provider'
import { ValidationButton, TalentPlaylistsPage } from 'smart-components'
import InitiativesSelectorDialogService from 'smart-components/initiatives-selector-dialog/Service'
import { InitiativeSelectorDialogState } from 'smart-components/initiatives-selector-dialog/store/State'
import { orderBy } from 'lodash'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import ValidationService from 'smart-components/validation-button/Service'

const MainMobileLayout: React.FC<IProps> = props => {
  const { disabled, hideMenuItem, pages } = props
  const classes = useStyles({})
  const { state, handlers } = useContext<ToolbarContext>(ToolbarContextProvider)
  const [anchors, setAnchors] = useState<IProps>({
    sidebarOpen: false,
    settingsMenuElement: null,
    featuredPage: []
  })
  const [initiativesSelectorState, setInitiativesSelectorState] = useState<InitiativeSelectorDialogState>(
    InitiativesSelectorDialogService.state
  )

  const settingsBtnRef = React.useRef()

  const mobilePages = orderBy(
    (pages || MENU_LIST).filter(menu => menu.visibleOnMobile && menu.path && !hideMenuItem[menu.id]),
    [menu => menu.id !== MENU_IDS.SNAPSHOT, menu => menu.id !== MENU_IDS.BASICS]
  )

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return
    }

    setAnchors({ ...anchors, sidebarOpen: open })
  }

  const openSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchors({ ...anchors, settingsMenuElement: event.currentTarget })
  }

  const closeSettings = () => {
    setAnchors({ ...anchors, settingsMenuElement: null })
  }

  const setFeaturedPage = (page: any) => {
    const { featuredPage } = anchors
    if (page) {
      featuredPage.push(page)
    } else {
      featuredPage.pop()
    }
    setAnchors({ ...anchors, featuredPage, settingsMenuElement: null })
  }

  const getLastFeaturedPage = () => {
    if (typeof anchors.featuredPage?.slice(-1)[0] === 'function') {
      return anchors.featuredPage?.slice(-1)[0]()
    }
    return anchors.featuredPage?.slice(-1)[0]
  }

  useEffect(() => {
    if (!disabled) {
      InitiativesSelectorDialogService.addListener(
        InitiativesSelectorDialogService.Events.onStateChange,
        setInitiativesSelectorState
      )
      return () => {
        InitiativesSelectorDialogService.removeListener(
          InitiativesSelectorDialogService.Events.onStateChange,
          setInitiativesSelectorState
        )
      }
    }
    return () => {}
  }, [])

  const SideList = () => (
    <div className={classes.menuList} role="sidebar" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
      <Grid container justify={'center'} alignItems={'flex-start'}>
        <img className={classes.sidebarLogo} src={pointrMinimalLogo} alt={'Pointr'} />
      </Grid>
      <List>
        {mobilePages.map(page => (
          <ListItem button key={page.id} component="a" href={`#${page.id}`}>
            <ListItemText primary={page.title} />
          </ListItem>
        ))}
      </List>
    </div>
  )

  const HideOnScroll = (props: IProps) => {
    const { children, window } = props
    const trigger = useScrollTrigger({ target: window ? window() : undefined })
    return (
      <Slide appear={false} direction="down" in={!trigger}>
        {children}
      </Slide>
    )
  }

  return (
    <Grid container className={classes.root}>
      <Background />
      <InitiativesSelectorDialog />
      <div className={classes.rootMobile}>
        <HideOnScroll {...props}>
          <AppBar position="fixed" className={classes.appBar}>
            <MdToolbar className={classes.toolBar}>
              <IconButton
                onClick={event =>
                  anchors.featuredPage?.length > 0 ? setFeaturedPage(null) : toggleDrawer(true)(event)
                }
                edge="start"
                className={classes.menuButton}
                aria-label="menu"
              >
                {anchors.featuredPage?.length > 0 ? <ArrowBack /> : <MenuIcon />}
              </IconButton>
              <div className={classes.logo}>
                <PointrLogo />
              </div>
              <ValidationButton />
              <IconButton
                edge="end"
                className={classes.settingsButton}
                ref={settingsBtnRef}
                aria-controls="settings-menu"
                aria-label="settings"
                onClick={openSettings}
              >
                <Settings />
              </IconButton>
              <Menu
                id="settings-menu"
                className={classes.menuSettings}
                getContentAnchorEl={null}
                anchorEl={() => settingsBtnRef.current}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                keepMounted
                open={Boolean(anchors.settingsMenuElement)}
                onClose={closeSettings}
              >
                <MenuItem
                  onClick={() => {
                    state.isMiniProfileComplete ? handlers.copyToClipboard() : ValidationService.setIsDialogOpen('OPEN')
                  }}
                >
                  Share profile link
                  {!state.isMiniProfileComplete && <HelpOutlineIcon className={classes.helpIcon} />}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    state.isComplete ? handlers.downloadPDF() : ValidationService.setIsDialogOpen('OPEN')
                  }}
                >
                  Download to PDF
                  {!state.isComplete && <HelpOutlineIcon className={classes.helpIcon} />}
                </MenuItem>
                {!disabled && initiativesSelectorState.initiativesOptions.length > 0 && (
                  <MenuItem
                    onClick={() => {
                      InitiativesSelectorDialogService.setIsDialogOpen('OPEN')
                      closeSettings()
                    }}
                  >
                    Pending initiatives
                  </MenuItem>
                )}
                {!disabled && state.displayPlaylists && (
                  <MenuItem
                    onClick={() =>
                      setFeaturedPage(<TalentPlaylistsPage onClickPlaylist={(page: any) => setFeaturedPage(page)} />)
                    }
                  >
                    Playlists
                  </MenuItem>
                )}
                {!disabled && <MenuItem onClick={handlers.onClickSettings}>Settings</MenuItem>}
                {!disabled && <MenuItem onClick={handlers.onLogOff}>Log out</MenuItem>}
              </Menu>
            </MdToolbar>
          </AppBar>
        </HideOnScroll>
        <MdToolbar />
        {anchors.featuredPage?.length > 0 ? (
          <div className={classes.content}>
            <div className={classes.component}>{getLastFeaturedPage()}</div>
          </div>
        ) : (
          <div className={classes.content}>
            <SwipeableDrawer
              className={classes.sidebar}
              open={anchors.sidebarOpen}
              onClose={toggleDrawer(false)}
              onOpen={toggleDrawer(true)}
            >
              {SideList()}
            </SwipeableDrawer>
            {mobilePages.map(page => (
              <Box display="flex" key={page.id} id={page.id}>
                <div className={classes.component}>
                  {page.displayComponent ? page.displayComponent : <page.component {...props} />}
                </div>
              </Box>
            ))}
          </div>
        )}
      </div>
    </Grid>
  )
}

export default (props: IProps) => (
  <Provider {...props}>
    <MainMobileLayout {...props} />
  </Provider>
)
